import React, { useState, useEffect, useRef, useCallback, useMemo, useDeferredValue } from 'react';
import { useDarkMode } from '../contexts/DarkModeContext';
import { useAudio } from '../contexts/AudioContext';
import { useAudioUI } from '../contexts/AudioUIContext';
import { useAuth } from '../services/AuthContext';
import { useShareLink } from '../contexts/ShareLinkContext';
import { openRouterService } from '../services/openRouterService';

import ReadableTranscriptView from './ReadableTranscriptView';
import { DeepgramResponse, SpeakerLabels, SpeakerSegment } from '../types';
import { Copy, Check, FileText, Subtitles, Maximize, Minimize, Search, X, ChevronLeft, ChevronRight, Cloud, Type, Wand2, FileSearch, GitCompare, Loader2, Upload, Activity, Settings2, LocateFixed } from 'lucide-react';
import { copyToClipboard } from '../utils/clipboard';
import {
  buildAuthenticatedSessionPdfUrl,
  buildSharePdfUrl,
} from '../utils/pdfLinks';
import WordCloudModal from './WordCloudModal';
import GapAnalysisModal from './GapAnalysisModal';
import TypographySettings, { TypographyConfig, loadTypographySettings } from './TypographySettings';
import TranscriptVersionOrbs from './TranscriptVersionOrbs';
import PdfBadgeIcon from './PdfBadgeIcon';
import ReactMarkdown from 'react-markdown';
import { useDevicePerformance } from '../hooks/useDevicePerformance';
import { startRefinementSession } from '../services/agentService';
import {
  getTranscriptRevisions,
  getTranscriptRevision,
  getTranscriptDiffgram,
  selectTranscriptRevision,
  deleteTranscriptRevision,
  getPublicTranscriptRevision,
  getPublicTranscriptDiffgram,
  TranscriptRevision,
  TranscriptDiffgram
} from '../services/transcriptRefinementService';

interface ReadableTranscriptTabProps {
  ttmlFiles?: any[];
  sessionId?: string | null;
  transcriptionChecksum?: string;
  selectedTranscriptChecksum?: string | null;
  transcript: string;
  filename: string;
  transcriptionData?: DeepgramResponse;
  speakerLabels?: SpeakerLabels;
  speakerSegments?: Map<number, SpeakerSegment[]>;
  duration?: number;
  youtubeUrl?: string | null;
  speakerCount: number;
  comments?: any[];
  onTranscriptChange?: (newTranscript: string) => void;
  onRestoreOriginal?: () => void;
  selectedTtmlId?: string | null;
  onFullscreenChange?: (isFullscreen: boolean) => void;
  onSearchMatchesChange?: (matches: any[]) => void;
  onActiveTranscriptionDataChange?: (data: DeepgramResponse | undefined) => void;
  onCurrentTopicChange?: (title: string | null) => void;
  showVideoPanel?: boolean;
  gapAnalysisData?: any;
  onFilteredGapClustersChange?: (clusters: any[], nonClusteredGaps?: any[]) => void;
}

const getTranscriptTextFromDeepgram = (data: DeepgramResponse | undefined, fallback: string): string => {
  if (!data?.results) {
    return fallback;
  }

  const channelTranscript = data.results.channels?.[0]?.alternatives?.[0]?.transcript;
  if (typeof channelTranscript === 'string' && channelTranscript.trim()) {
    return channelTranscript.trim();
  }

  if (Array.isArray(data.results.utterances) && data.results.utterances.length > 0) {
    const utteranceTranscript = data.results.utterances
      .map((utterance: any) => utterance?.transcript?.trim())
      .filter(Boolean)
      .join('\n\n');

    if (utteranceTranscript) {
      return utteranceTranscript;
    }
  }

  return fallback;
};

const ReadableTranscriptTab: React.FC<ReadableTranscriptTabProps> = ({
  ttmlFiles = [],
  sessionId,
  transcriptionChecksum,
  selectedTranscriptChecksum: selectedTranscriptChecksumProp = null,
  transcript,
  filename,
  transcriptionData,
  speakerLabels,
  speakerSegments,
  youtubeUrl,
  comments = [],
  selectedTtmlId,
  onFullscreenChange,
  onSearchMatchesChange,
  onActiveTranscriptionDataChange,
  onCurrentTopicChange,
  showVideoPanel = false,
  gapAnalysisData,
  onFilteredGapClustersChange
}) => {
  const { isDarkMode } = useDarkMode();
  const { pause, playSegment, isPlaying } = useAudio();
  const { cursorSyncEnabled, requestCursorSync } = useAudioUI();
  const { token } = useAuth();
  const perfSettings = useDevicePerformance();
  const isCompactTouchLayout = perfSettings.isCompactTouchLayout;
  const { shareToken } = useShareLink();
  const isPublicShare = !!shareToken;
  const isEditedMode = false;
  const hasEditCuts = false;
  const timelineMapper: any = null;
  const [selectedTtmlContent, setSelectedTtmlContent] = useState<string | null>(null);
  const [selectedTtmlName, setSelectedTtmlName] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMatches, setSearchMatches] = useState<any[]>([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [showMobileSearchModal, setShowMobileSearchModal] = useState(false);
  const [showWordCloud, setShowWordCloud] = useState(false);
  const [gapModalOpen, setGapModalOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const desktopToolbarMenuRef = useRef<HTMLDivElement>(null);
  const typographyButtonRef = useRef<HTMLButtonElement>(null);
  const [shouldNavigateToMatch, setShouldNavigateToMatch] = useState(false);
  const [hasInitializedSearch, setHasInitializedSearch] = useState(false);
  const [isUploadingToYouTube, setIsUploadingToYouTube] = useState(false);
  const [youtubeUploadStatus, setYoutubeUploadStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [showDesktopToolbarMenu, setShowDesktopToolbarMenu] = useState(false);
  const [showTypographySettings, setShowTypographySettings] = useState(false);
  const [typographyConfig, setTypographyConfig] = useState<TypographyConfig>(loadTypographySettings);
  const deferredTypographyConfig = useDeferredValue(typographyConfig);

  // Transcript revision state
  const [transcriptRevisions, setTranscriptRevisions] = useState<TranscriptRevision[]>([]);
  const [selectedTranscriptChecksum, setSelectedTranscriptChecksum] = useState<string | null>(null);
  const [isRefining, setIsRefining] = useState(false);
  const [showRevisionNotesModal, setShowRevisionNotesModal] = useState(false);
  const [revisionNotesContent, setRevisionNotesContent] = useState<string | null>(null);
  const [showDiffgramModal, setShowDiffgramModal] = useState(false);
  const [currentDiffgram, setCurrentDiffgram] = useState<TranscriptDiffgram | null>(null);
  // Note: Initialize as undefined, NOT transcriptionData - otherwise the fallback logic won't work
  // The loadRevisions effect will populate this with the selected revision's data
  const [activeTranscriptionData, setActiveTranscriptionData] = useState<DeepgramResponse | undefined>(undefined);
  // Track whether we've attempted to load revision data (to avoid using stale prop data as fallback)
  const [hasAttemptedRevisionLoad, setHasAttemptedRevisionLoad] = useState(false);

  // PDF export state
  const [pdfStatus, setPdfStatus] = useState<'cached' | 'generating' | 'not-generated' | 'error'>('not-generated');
  const [pdfJobId, setPdfJobId] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);

  // API base URL for PDF endpoints
  const API_BASE_URL = process.env.NODE_ENV === 'production'
    ? '/api'
    : 'http://localhost:7777/api';

  // Helper to get speaker name from labels or fallback to "Speaker X"
  const getSpeakerName = useCallback((speakerId: number | undefined): string => {
    if (speakerId === undefined) return '';
    const label = speakerLabels?.[speakerId];
    return label || `Speaker ${speakerId}`;
  }, [speakerLabels]);

  // Helper to format timestamp as HH:MM:SS
  const formatTimestampHHMMSS = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}h ${mins}m ${secs}s`;
    }
    if (mins > 0) {
      return `${mins}m ${secs}s`;
    }
    return `${secs}s`;
  };

  // Generate grouped transcript with speaker changes and periodic time markers
  const generateGroupedTranscript = useCallback((utterances: any[], getTime: (utt: any) => number) => {
    if (!utterances || utterances.length === 0) return '';

    const result: string[] = [];
    let nextTimeMarker = 10; // First time marker at 10 seconds
    const TIME_MARKER_INTERVAL = 10; // seconds

    // First pass: group utterances by speaker and collect their text
    interface SpeakerTurn {
      speaker: number | undefined;
      speakerName: string;
      startTime: number;
      segments: { text: string; startTime: number; endTime: number }[];
    }

    const turns: SpeakerTurn[] = [];
    let currentTurn: SpeakerTurn | null = null;

    for (const utt of utterances) {
      const startTime = getTime(utt);
      const endTime = utt.end ?? startTime + 5; // Estimate if no end time
      const speakerId = utt.speaker;

      if (!currentTurn || speakerId !== currentTurn.speaker) {
        // Start a new turn
        currentTurn = {
          speaker: speakerId,
          speakerName: speakerId !== undefined ? getSpeakerName(speakerId) : '',
          startTime: startTime,
          segments: []
        };
        turns.push(currentTurn);
      }

      currentTurn.segments.push({
        text: utt.transcript,
        startTime: startTime,
        endTime: endTime
      });
    }

    // Second pass: output with time markers
    for (const turn of turns) {
      // Add spacing before speaker (except first)
      if (result.length > 0) {
        result.push('');
        result.push('');
      }

      // Speaker header
      result.push(`${turn.speakerName} [${formatTimestampHHMMSS(turn.startTime)}]:`);

      // Collect all text for this turn, inserting time markers at appropriate points
      let turnText = '';

      for (const segment of turn.segments) {
        // Check if we need to insert time markers before this segment
        while (nextTimeMarker <= segment.startTime) {
          // Insert marker inline with the text
          if (turnText.length > 0) {
            turnText += ` [${formatTimestampHHMMSS(nextTimeMarker)}] `;
          }
          nextTimeMarker += TIME_MARKER_INTERVAL;
        }

        // Add segment text
        if (turnText.length > 0 && !turnText.endsWith(' ')) {
          turnText += ' ';
        }
        turnText += segment.text;
      }

      result.push(turnText.trim());
    }

    return result.join('\n');
  }, [getSpeakerName]);

  // Generate edit-aware transcript for copy (excludes cut segments, uses speaker names)
  // Uses activeTranscriptionData (from selected revision) or falls back to transcriptionData
  // IMPORTANT: Only use transcriptionData as fallback AFTER we've attempted to load revision data
  // This prevents using stale data when the component remounts
  const editedTranscript = useMemo(() => {
    // Only use transcriptionData as fallback if:
    // 1. We've already attempted to load revisions (hasAttemptedRevisionLoad), OR
    // 2. There's no session (no revisions to load), OR
    // 3. There's no token/shareToken (can't load revisions)
    const canUseFallback = hasAttemptedRevisionLoad || !sessionId || (!token && !shareToken);
    const dataToUse = activeTranscriptionData || (canUseFallback ? transcriptionData : undefined);

    if (!dataToUse?.results?.utterances) {
      return transcript;
    }

    if (!isEditedMode || !timelineMapper || !hasEditCuts) {
      // Non-edit mode: use original times
      return generateGroupedTranscript(
        dataToUse.results.utterances,
        (utt) => utt.start
      );
    }

    // Edit mode: filter cut regions and remap times
    const filteredUtterances = dataToUse.results.utterances.filter((utt: any) => {
      return !timelineMapper.isFullyInCutRegion(utt.start, utt.end);
    });

    return generateGroupedTranscript(
      filteredUtterances,
      (utt) => {
        let editedStart = timelineMapper.originalToEdited(utt.start);
        if (editedStart === null) {
          const cut = timelineMapper.getCutAt(utt.start);
          if (cut) {
            editedStart = timelineMapper.originalToEdited(cut.endTime);
          }
        }
        return editedStart ?? utt.start;
      }
    );
  }, [isEditedMode, timelineMapper, hasEditCuts, activeTranscriptionData, transcriptionData, transcript, generateGroupedTranscript, hasAttemptedRevisionLoad, sessionId, token, shareToken]);

  // Generate edit-aware transcription data for SRT (filters and remaps utterances)
  // Uses activeTranscriptionData (from selected revision) or falls back to transcriptionData
  // IMPORTANT: Only use transcriptionData as fallback AFTER we've attempted to load revision data
  // EXCEPTION: If we already have valid transcriptionData but no active revision, allow it as fallback
  const editedTranscriptionData = useMemo(() => {
    const canUseFallback = hasAttemptedRevisionLoad || !sessionId || (!token && !shareToken) ||
                           (!activeTranscriptionData && transcriptionData?.results?.utterances);
    const dataToUse = activeTranscriptionData || (canUseFallback ? transcriptionData : undefined);
    if (!isEditedMode || !timelineMapper || !hasEditCuts || !dataToUse?.results?.utterances) {
      return dataToUse;
    }

    // Deep clone and filter/remap utterances
    const filtered = dataToUse.results.utterances
      .filter((utt: any) => !timelineMapper.isFullyInCutRegion(utt.start, utt.end))
      .map((utt: any) => {
        let editedStart = timelineMapper.originalToEdited(utt.start);
        let editedEnd = timelineMapper.originalToEdited(utt.end);

        // Handle edge case: start in cut, end outside
        if (editedStart === null && editedEnd !== null) {
          const cut = timelineMapper.getCutAt(utt.start);
          if (cut) {
            editedStart = timelineMapper.originalToEdited(cut.endTime);
          }
        }

        return {
          ...utt,
          start: editedStart ?? utt.start,
          end: editedEnd ?? utt.end,
          words: utt.words?.map((w: any) => {
            const wStart = timelineMapper.originalToEdited(w.start);
            const wEnd = timelineMapper.originalToEdited(w.end);
            return {
              ...w,
              start: wStart ?? w.start,
              end: wEnd ?? w.end
            };
          }).filter((w: any) => w.start !== null)
        };
      });

    return {
      ...dataToUse,
      results: {
        ...dataToUse.results,
        utterances: filtered
      }
    };
  }, [isEditedMode, timelineMapper, hasEditCuts, activeTranscriptionData, transcriptionData, hasAttemptedRevisionLoad, sessionId, token, shareToken]);

  // Use the selected TTML from the session (linked to TTML Ranking tab)
  // Fetch content from API if not already loaded in the file object
  useEffect(() => {
    const loadTtmlContent = async () => {
      if (!ttmlFiles || ttmlFiles.length === 0) {
        setSelectedTtmlName(null);
        setSelectedTtmlContent(null);
        return;
      }

      // Find the file to use
      let selectedFile = null;
      if (selectedTtmlId) {
        selectedFile = ttmlFiles.find(file =>
          file.checksum === selectedTtmlId ||
          (file.filename || file.name) === selectedTtmlId
        );
      }
      if (!selectedFile) {
        selectedFile = ttmlFiles[0];
      }

      if (!selectedFile) {
        setSelectedTtmlName(null);
        setSelectedTtmlContent(null);
        return;
      }

      setSelectedTtmlName(selectedFile.filename || selectedFile.name);

      // If content is already available, use it
      if (selectedFile.content) {
        setSelectedTtmlContent(selectedFile.content);
        return;
      }

      // Otherwise, fetch content from API
      if (selectedFile.checksum) {
        setIsLoadingContent(true);
        try {
          let content: string | null = null;

          // If we have an auth token, use the authenticated endpoint
          if (token) {
            const blob = await openRouterService.downloadCachedTTML(selectedFile.checksum, token);
            content = await blob.text();
          }
          // Otherwise, use the public share endpoint
          else if (shareToken) {
            const response = await fetch(`${API_BASE_URL}/api/public/share/${shareToken}/ttml-content/${selectedFile.checksum}`);
            if (response.ok) {
              content = await response.text();
            } else {
              throw new Error(`Failed to fetch TTML: ${response.status}`);
            }
          }

          if (content) {
            setSelectedTtmlContent(content);
          } else {
            setSelectedTtmlContent(null);
          }
        } catch (error) {
          console.error('[ReadableTranscriptTab] Failed to fetch TTML content:', error);
          setSelectedTtmlContent(null);
        } finally {
          setIsLoadingContent(false);
        }
      } else {
        setSelectedTtmlContent(null);
      }
    };

    loadTtmlContent();
  }, [ttmlFiles, selectedTtmlId, token, shareToken, API_BASE_URL]);

  // Track previous TTML ID to detect actual changes
  const prevTtmlIdRef = useRef<string | null>(null);

  const canUsePdfExport = !!sessionId && (!!token || !!shareToken);

  // Check PDF status when sessionId or selectedTtmlId changes
  useEffect(() => {
    if (!sessionId) {
      if (prevTtmlIdRef.current !== null) {
        setPdfStatus('not-generated');
        setPdfJobId(null);
        setPdfError(null);
        prevTtmlIdRef.current = null;
      }
      return;
    }

    if (!token) {
      return;
    }

    const pdfStateKey = selectedTtmlId || '__session_pdf__';
    if (prevTtmlIdRef.current !== pdfStateKey) {
      setPdfStatus('not-generated');
      setPdfJobId(null);
      setPdfError(null);
      prevTtmlIdRef.current = pdfStateKey;
    }

    const checkPdfStatus = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}/pdf/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) {
          return;
        }

        const data = await response.json();
        if (data.status === 'cached' || data.status === 'generating') {
          setPdfStatus(data.status);
          if (data.jobId) setPdfJobId(data.jobId);
        }
      } catch (error) {
        console.error('[PDF] Error checking status:', error);
      }
    };

    checkPdfStatus();
  }, [sessionId, selectedTtmlId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll for PDF job completion
  useEffect(() => {
    if (!pdfJobId || pdfStatus !== 'generating' || !sessionId || !token) return;

    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}/pdf/jobs/${pdfJobId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) return;

        const data = await response.json();

        if (data.status === 'completed') {
          setPdfStatus('cached');
          setPdfJobId(null);
        } else if (data.status === 'failed') {
          setPdfStatus('error');
          setPdfError(data.error || 'PDF generation failed');
          setPdfJobId(null);
        }
      } catch (error) {
        console.error('[PDF] Error polling job status:', error);
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [pdfJobId, pdfStatus, sessionId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const pdfActionLink = useMemo(() => {
    if (!sessionId) return null;
    if (shareToken && !token) {
      return buildSharePdfUrl(shareToken, API_BASE_URL, window.location.origin);
    }
    return buildAuthenticatedSessionPdfUrl(sessionId, API_BASE_URL);
  }, [sessionId, shareToken, token, API_BASE_URL]);

  // Handle PDF download/generation
  const handleDownloadPDF = async () => {
    if (!sessionId || (!token && !shareToken) || !pdfActionLink) return;

    if (shareToken && !token) {
      window.open(pdfActionLink, '_blank', 'noopener');
      return;
    }

    if (pdfStatus === 'cached') {
      window.open(pdfActionLink, '_blank', 'noopener');
      return;
    }

    // Prevent duplicate calls while generating
    if (pdfStatus === 'generating') return;

    // Trigger generation
    setPdfStatus('generating');
    setPdfError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}/pdf/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ force: false })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to start PDF generation');
      }

      const data = await response.json();

      if (data.status === 'cached') {
        setPdfStatus('cached');
        window.open(pdfActionLink, '_blank', 'noopener');
      } else {
        setPdfJobId(data.jobId);
      }
    } catch (error) {
      console.error('[PDF] Error generating PDF:', error);
      setPdfStatus('error');
      setPdfError(error instanceof Error ? error.message : 'Failed to generate PDF');
    }
  };

  const pdfActionTitle = pdfStatus === 'error'
    ? pdfError || 'PDF export unavailable'
    : pdfStatus === 'generating'
      ? 'Generating PDF transcript...'
      : pdfStatus === 'cached'
        ? 'Download PDF transcript'
        : 'Generate PDF transcript';

  // Handle play segment callback
  const handlePlaySegment = async (startTime: number, endTime?: number) => {
    // Use the audio context's playSegment method which handles everything
    await playSegment(startTime, endTime);
  };

  // Handle pause segment callback
  const handlePauseSegment = () => {
    pause();
  };

  // Handle search updates from ReadableTranscriptView - stable callback to prevent infinite loops
  // Use a ref to track previous match count to avoid dependencies on state
  const prevMatchCountRef = useRef(0);
  const handleSearchUpdate = useCallback((matches: any[]) => {
    const prevMatchCount = prevMatchCountRef.current;
    prevMatchCountRef.current = matches.length;

    setSearchMatches(matches);

    // Update current match index if needed
    if (matches.length === 0) {
      setCurrentMatchIndex(0);
    } else {
      setCurrentMatchIndex(prev => prev >= matches.length ? matches.length - 1 : prev);
    }

    // Navigate to first match when we get new matches (from 0 to > 0)
    if (prevMatchCount === 0 && matches.length > 0) {
      setShouldNavigateToMatch(true);
      setHasInitializedSearch(true);
    }
  }, []);

  // Button handlers
  const handleDownload = () => {
    // Always use editedTranscript which handles both edit-mode timestamps and speaker label mapping
    const blob = new Blob([editedTranscript], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const suffix = isEditedMode ? '_transcript_edited.txt' : '_transcript.txt';
    a.download = `${filename.replace(/\.[^/.]+$/, "")}${suffix}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fetchBackendSrt = useCallback(async () => {
    if (!sessionId) {
      throw new Error('Session ID is required for SRT export');
    }

    const params = new URLSearchParams();
    if (selectedTranscriptChecksum) {
      params.set('revision', selectedTranscriptChecksum);
    }

    const requestUrl = isPublicShare && shareToken
      ? `${API_BASE_URL}/public/share/${shareToken}/transcript/srt${params.toString() ? `?${params.toString()}` : ''}`
      : `${API_BASE_URL}/v1/episodes/${sessionId}/transcript/srt${params.toString() ? `?${params.toString()}` : ''}`;
    const headers: Record<string, string> = {};
    if (!isPublicShare && token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(requestUrl, {
      headers,
      credentials: isPublicShare ? 'omit' : undefined,
    });
    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || `Failed to generate SRT (${response.status})`);
    }

    return response.text();
  }, [API_BASE_URL, isPublicShare, selectedTranscriptChecksum, sessionId, shareToken, token]);

  const handleDownloadSRT = async () => {
    try {
      if (!sessionId) {
        alert('Session ID is required for SRT export');
        return;
      }

      const srtContent = await fetchBackendSrt();

      const blob = new Blob([srtContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filename.replace(/\.[^/.]+$/, "")}_captions.srt`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating SRT:', error);
      alert('Failed to generate SRT file. Please try again.');
    }
  };

  // Upload SRT captions to YouTube
  const handleUploadToYouTube = useCallback(async () => {
    if (!token || !sessionId) {
      alert('You must be logged in to upload captions to YouTube.');
      return;
    }

    try {
      setIsUploadingToYouTube(true);
      setYoutubeUploadStatus('idle');

      const srtContent = await fetchBackendSrt();

      // Upload to YouTube via API
      const API_BASE_URL = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:7777/api';
      const response = await fetch(`${API_BASE_URL}/youtube-analytics/captions/${sessionId}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          srtContent,
          language: 'en-US'
        })
      });

      const result = await response.json();

      if (!response.ok) {
        if (result.needsAuth) {
          const shouldOpenSettings = window.confirm(
            'Please connect your YouTube account first. Open Settings > Personal & YouTube now?'
          );
          if (shouldOpenSettings) {
            window.location.assign('/settings?pane=personal');
          }
        } else {
          throw new Error(result.error || result.message || 'Failed to upload captions');
        }
        setYoutubeUploadStatus('error');
        return;
      }

      setYoutubeUploadStatus('success');
      setTimeout(() => setYoutubeUploadStatus('idle'), 3000);
    } catch (error) {
      console.error('Error uploading to YouTube:', error);
      setYoutubeUploadStatus('error');
      alert(error instanceof Error ? error.message : 'Failed to upload captions to YouTube');
    } finally {
      setIsUploadingToYouTube(false);
    }
  }, [fetchBackendSrt, sessionId, token]);

  const handleCopyToClipboard = async () => {
    // Use editedTranscript which handles both edit-mode and speaker label mapping
    const success = await copyToClipboard(editedTranscript);
    if (success) {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } else {
      alert('Failed to copy transcript to clipboard');
    }
  };

  const toggleFullscreen = () => {
    const newValue = !isFullscreen;
    setIsFullscreen(newValue);
    if (onFullscreenChange) {
      onFullscreenChange(newValue);
    }
  };

  // Reset search initialization when query changes
  useEffect(() => {
    if (searchQuery === '') {
      setHasInitializedSearch(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    if (!isCompactTouchLayout || !showMobileSearchModal) {
      return;
    }

    const focusTimer = window.setTimeout(() => {
      searchInputRef.current?.focus({ preventScroll: true });
      searchInputRef.current?.select();
    }, 120);

    return () => window.clearTimeout(focusTimer);
  }, [isCompactTouchLayout, showMobileSearchModal]);

  useEffect(() => {
    if (!isCompactTouchLayout && showMobileSearchModal) {
      setShowMobileSearchModal(false);
    }
  }, [isCompactTouchLayout, showMobileSearchModal]);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setSearchMatches([]);
    setCurrentMatchIndex(0);
    setHasInitializedSearch(false);
  }, []);

  const closeDesktopToolbarMenu = useCallback(() => {
    setShowDesktopToolbarMenu(false);
    setShowTypographySettings(false);
  }, []);

  useEffect(() => {
    if (!showDesktopToolbarMenu) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (desktopToolbarMenuRef.current && !desktopToolbarMenuRef.current.contains(event.target as Node)) {
        closeDesktopToolbarMenu();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeDesktopToolbarMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showDesktopToolbarMenu, closeDesktopToolbarMenu]);

  // Transcript refinement is now always agent-based: start immediately and
  // hand off to the agent tab for supervision.
  const handleStartRefinement = async () => {
    if (!sessionId || !token) return;

    setIsRefining(true);

    try {
      const result = await startRefinementSession({
        sessionId,
        enableWebSearch: true,
      }, token);

      window.dispatchEvent(new CustomEvent('navigate-to-agent', {
        detail: { agentSessionId: result.agentSessionId },
      }));
    } catch (error) {
      console.error('[ReadableTranscriptTab] Failed to start transcript refinement agent:', error);
    } finally {
      setIsRefining(false);
    }
  };

  // Load transcription data for a specific revision
  // Works for both authenticated users and public shares
  const loadRevisionData = useCallback(async (checksum: string) => {
    if (!sessionId) return;

    try {
      let deepgram;
      if (shareToken) {
        // Public share: use public API
        const result = await getPublicTranscriptRevision(shareToken, checksum);
        deepgram = result.deepgram;
      } else if (token) {
        // Authenticated: use authenticated API
        const result = await getTranscriptRevision(sessionId, checksum, token);
        deepgram = result.deepgram;
      } else {
        return;
      }
      setActiveTranscriptionData(deepgram);
    } catch (error) {
      console.error('[ReadableTranscriptTab] Failed to load revision data:', error);
    }
  }, [sessionId, shareToken, token]);

  // Load transcript revisions for authenticated users.
  const refreshTranscriptRevisions = useCallback(async () => {
    if (!sessionId) return;

    if (isPublicShare) {
      setTranscriptRevisions([]);
      const publicSelectedChecksum = selectedTranscriptChecksumProp || transcriptionChecksum || null;
      setSelectedTranscriptChecksum(publicSelectedChecksum);
      if (publicSelectedChecksum && publicSelectedChecksum !== transcriptionChecksum) {
        await loadRevisionData(publicSelectedChecksum);
      } else {
        setActiveTranscriptionData(transcriptionData);
      }
      setCurrentDiffgram(null);
      setShowDiffgramModal(false);
      setRevisionNotesContent(null);
      setShowRevisionNotesModal(false);
      setHasAttemptedRevisionLoad(true);
      return;
    }

    try {
      if (!token) {
        setHasAttemptedRevisionLoad(true);
        return;
      }

      const { revisions, selectedChecksum } = await getTranscriptRevisions(sessionId, token);
      setTranscriptRevisions(revisions);
      setSelectedTranscriptChecksum(selectedChecksum);

      if (selectedChecksum) {
        await loadRevisionData(selectedChecksum);
      } else {
        setActiveTranscriptionData(undefined);
      }

      setHasAttemptedRevisionLoad(true);
    } catch (error) {
      console.error('[ReadableTranscriptTab] Failed to load transcript revisions:', error);
      setHasAttemptedRevisionLoad(true);
    }
  }, [isPublicShare, loadRevisionData, selectedTranscriptChecksumProp, sessionId, token, transcriptionChecksum, transcriptionData]);

  useEffect(() => {
    void refreshTranscriptRevisions();
  }, [refreshTranscriptRevisions]);

  useEffect(() => {
    if (!sessionId || isPublicShare) {
      return undefined;
    }

    const scopesRequireRefresh = (scopes: unknown): boolean => {
      if (!Array.isArray(scopes) || scopes.length === 0) {
        return true;
      }
      const normalized = scopes.map((scope) => String(scope || '').trim());
      return normalized.some((scope) => (
        scope === 'session' ||
        scope === 'transcript' ||
        scope === 'transcript_revisions' ||
        scope === 'agent_done'
      ));
    };

    const handleSessionRefresh = (event: Event) => {
      const detail = (event as CustomEvent).detail || {};
      if (detail.sessionId && detail.sessionId !== sessionId) {
        return;
      }
      if (!scopesRequireRefresh(detail.scopes)) {
        return;
      }
      void refreshTranscriptRevisions();
    };

    window.addEventListener('rescript-session-refresh-needed', handleSessionRefresh as EventListener);
    window.addEventListener('rescript-session-invalidated', handleSessionRefresh as EventListener);

    return () => {
      window.removeEventListener('rescript-session-refresh-needed', handleSessionRefresh as EventListener);
      window.removeEventListener('rescript-session-invalidated', handleSessionRefresh as EventListener);
    };
  }, [isPublicShare, refreshTranscriptRevisions, sessionId]);

  // View revision notes
  const handleViewRevisionNotes = (checksum: string) => {
    const revision = transcriptRevisions.find(r => r.checksum === checksum);
    if (revision?.revision_notes) {
      setRevisionNotesContent(revision.revision_notes);
      setShowRevisionNotesModal(true);
    }
  };

  // View diffgram for a revision
  // Works for both authenticated users and public shares
  const handleViewDiffgram = async (checksum: string) => {
    if (!sessionId) return;

    try {
      let diffgram;
      if (shareToken) {
        // Public share: use public API
        const result = await getPublicTranscriptDiffgram(shareToken, checksum);
        diffgram = result.diffgram;
      } else if (token) {
        // Authenticated: use authenticated API
        const result = await getTranscriptDiffgram(sessionId, checksum, token);
        diffgram = result.diffgram;
      } else {
        return;
      }
      if (diffgram) {
        setCurrentDiffgram(diffgram);
        setShowDiffgramModal(true);
      }
    } catch (error) {
      console.error('[ReadableTranscriptTab] Failed to load diffgram:', error);
    }
  };

  // Handle version selection
  // For authenticated users: persists selection to server
  // For public shares: only updates local state (not persisted)
  const handleSelectRevision = async (checksum: string) => {
    if (!sessionId) return;

    try {
      if (shareToken) {
        // Public share: just update local state (no persistence)
        setSelectedTranscriptChecksum(checksum);
        await loadRevisionData(checksum);
      } else if (token) {
        // Authenticated: persist to server and update local state
        await selectTranscriptRevision(sessionId, checksum, token);
        setSelectedTranscriptChecksum(checksum);
        await loadRevisionData(checksum);
      }
    } catch (error) {
      console.error('[ReadableTranscriptTab] Failed to select revision:', error);
    }
  };

  // Handle revision deletion
  const handleDeleteRevision = async (checksum: string) => {
    if (!sessionId || !token) return;

    try {
      const result = await deleteTranscriptRevision(sessionId, checksum, token);

      // Refresh the revisions list
      const { revisions, selectedChecksum } = await getTranscriptRevisions(sessionId, token);
      setTranscriptRevisions(revisions);

      // If the deleted revision was selected, switch to the new selected checksum
      if (checksum === selectedTranscriptChecksum && result.newSelectedChecksum) {
        setSelectedTranscriptChecksum(result.newSelectedChecksum);
        await loadRevisionData(result.newSelectedChecksum);
      } else if (selectedChecksum) {
        setSelectedTranscriptChecksum(selectedChecksum);
      }
    } catch (error) {
      console.error('[ReadableTranscriptTab] Failed to delete revision:', error);
    }
  };

  // Notify parent when search matches change (for timeline markers)
  useEffect(() => {
    if (onSearchMatchesChange) {
      onSearchMatchesChange(searchMatches);
    }
  }, [searchMatches, onSearchMatchesChange]);

  // Handle ESC key to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isFullscreen && event.key === 'Escape') {
        setIsFullscreen(false);
        if (onFullscreenChange) {
          onFullscreenChange(false);
        }
      }
    };

    if (isFullscreen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullscreen, onFullscreenChange]);

  // Check if we have transcription data but no TTML files
  // Use activeTranscriptionData (selected revision) if available, otherwise fallback to prop
  const effectiveTranscriptionData = activeTranscriptionData || (hasAttemptedRevisionLoad ? transcriptionData : undefined);
  const effectiveTranscriptText = useMemo(
    () => getTranscriptTextFromDeepgram(effectiveTranscriptionData, transcript),
    [effectiveTranscriptionData, transcript]
  );
  const hasTranscriptionData = !!(effectiveTranscriptionData && effectiveTranscriptionData.results);
  const hasRenderableTranscript = hasTranscriptionData || Boolean(selectedTtmlContent);
  const isWaitingForAuthenticatedRevisionBootstrap = Boolean(
    sessionId
    && token
    && !isPublicShare
    && !hasAttemptedRevisionLoad
  );
  const isTranscriptInitializing = Boolean(
    isLoadingContent
    || isWaitingForAuthenticatedRevisionBootstrap
    || (sessionId && !hasAttemptedRevisionLoad && !hasRenderableTranscript)
  );

  useEffect(() => {
    onActiveTranscriptionDataChange?.(effectiveTranscriptionData || transcriptionData);
  }, [effectiveTranscriptionData, onActiveTranscriptionDataChange, transcriptionData]);
  
  if (!isTranscriptInitializing && !selectedTtmlContent && ttmlFiles.length === 0 && !hasTranscriptionData) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 dark:text-gray-400">
            No metadata revision available. Generate one from the Ranking and Metadata tab first.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-full flex flex-col ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
      {/* Stats and Buttons Section - Hidden in fullscreen and on mobile */}
      {!isFullscreen && !isCompactTouchLayout && (
        <div className={`flex-shrink-0 p-4 border-b ${
          isDarkMode ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className="flex items-start gap-3">
            <div className="relative min-w-0 flex-1">
              <div className={`flex items-center gap-2 rounded-xl border px-3 py-2 ${
                isDarkMode ? 'border-gray-700 bg-gray-800/80' : 'border-gray-200 bg-gray-50'
              }`}>
                <Search size={15} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && searchQuery.trim() !== '') {
                      setShouldNavigateToMatch(true);
                    }
                  }}
                  placeholder="Search transcript..."
                  className={`min-w-0 flex-1 bg-transparent text-sm outline-none ${
                    isDarkMode ? 'text-gray-100 placeholder-gray-500' : 'text-gray-700 placeholder-gray-400'
                  }`}
                />
                {searchMatches.length > 0 && (
                  <div className={`flex items-center gap-1 rounded-full px-2 py-1 text-xs ${
                    isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-white text-gray-600'
                  }`}>
                    <span>{currentMatchIndex + 1}/{searchMatches.length}</span>
                    <button
                      onClick={() => {
                        setCurrentMatchIndex((prev) => (prev - 1 + searchMatches.length) % searchMatches.length);
                        setShouldNavigateToMatch(true);
                      }}
                      className={`rounded p-0.5 ${isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-100'}`}
                      title="Previous match"
                    >
                      <ChevronLeft size={13} />
                    </button>
                    <button
                      onClick={() => {
                        setCurrentMatchIndex((prev) => (prev + 1) % searchMatches.length);
                        setShouldNavigateToMatch(true);
                      }}
                      className={`rounded p-0.5 ${isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-100'}`}
                      title="Next match"
                    >
                      <ChevronRight size={13} />
                    </button>
                  </div>
                )}
                {searchQuery && (
                  <button
                    onClick={clearSearch}
                    className={`rounded p-1 ${isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}
                    title="Clear search"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>

            <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
              {!perfSettings.isMobile && !cursorSyncEnabled && (
                <button
                  type="button"
                  onClick={() => requestCursorSync()}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-brand-yellow text-brand-navy transition-colors hover:bg-[#f6c15e]"
                  title="Resync transcript and timeline to the playhead"
                  aria-label="Resync transcript and timeline to the playhead"
                >
                  <LocateFixed size={16} />
                </button>
              )}

              <button
                onClick={handleCopyToClipboard}
                className={`inline-flex h-10 w-10 items-center justify-center rounded-xl text-sm font-medium transition-colors ${
                  isDarkMode
                    ? 'bg-gray-700 text-gray-100 hover:bg-gray-600'
                    : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                }`}
                title={isCopied ? 'Copied transcript to clipboard' : 'Copy transcript'}
                aria-label={isCopied ? 'Copied transcript to clipboard' : 'Copy transcript'}
              >
                {isCopied ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
              </button>

              <div className="relative">
                <button
                  ref={typographyButtonRef}
                  type="button"
                  onClick={() => {
                    setShowDesktopToolbarMenu(false);
                    setShowTypographySettings((prev) => !prev);
                  }}
                  className={`inline-flex h-10 w-10 items-center justify-center rounded-xl text-sm font-medium transition-colors ${
                    showTypographySettings
                      ? isDarkMode
                        ? 'bg-gray-700 text-white'
                        : 'bg-gray-200 text-gray-900'
                      : isDarkMode
                        ? 'bg-gray-800 text-gray-200 hover:bg-gray-700'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  title="Reading settings"
                  aria-label="Reading settings"
                >
                  <Type size={16} />
                </button>

                <TypographySettings
                  config={typographyConfig}
                  onChange={setTypographyConfig}
                  isOpen={showTypographySettings}
                  onClose={() => setShowTypographySettings(false)}
                  anchorRef={typographyButtonRef}
                />
              </div>

              {canUsePdfExport && (
                <button
                  type="button"
                  onClick={handleDownloadPDF}
                  disabled={pdfStatus === 'generating'}
                  title={pdfActionTitle}
                  aria-label={pdfActionTitle}
                  className={`relative inline-flex h-10 w-10 items-center justify-center rounded-xl text-sm font-medium transition-colors ${
                    isDarkMode
                      ? 'bg-gray-800 text-gray-200 hover:bg-gray-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  } ${pdfStatus === 'generating' ? 'cursor-wait opacity-70' : ''}`}
                >
                  <PdfBadgeIcon isDarkMode={isDarkMode} />
                  {pdfStatus === 'generating' && <Loader2 size={14} className="animate-spin" />}
                </button>
              )}

              <button
                type="button"
                onClick={toggleFullscreen}
                className={`inline-flex h-10 w-10 items-center justify-center rounded-xl text-sm font-medium transition-colors ${
                  isDarkMode ? 'bg-gray-800 text-gray-200 hover:bg-gray-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              >
                {isFullscreen ? <Minimize size={15} /> : <Maximize size={15} />}
              </button>

              <div className="relative" ref={desktopToolbarMenuRef}>
                <button
                  type="button"
                  onClick={() => setShowDesktopToolbarMenu((prev) => !prev)}
                  className={`inline-flex h-10 w-10 items-center justify-center rounded-xl text-sm font-medium transition-colors ${
                    showDesktopToolbarMenu
                      ? isDarkMode
                        ? 'bg-gray-700 text-white'
                        : 'bg-gray-200 text-gray-900'
                      : isDarkMode
                        ? 'bg-gray-800 text-gray-200 hover:bg-gray-700'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  title="Transcript controls"
                  aria-label="Transcript controls"
                >
                  <Settings2 size={16} />
                </button>

                {showDesktopToolbarMenu && (
                  <div className={`absolute right-0 top-[calc(100%+0.75rem)] z-40 w-[26rem] rounded-2xl border p-4 shadow-2xl ${
                    isDarkMode ? 'border-gray-700 bg-gray-900 text-gray-100' : 'border-gray-200 bg-white text-gray-900'
                  }`}>
                    <div className="space-y-4">
                      {!isPublicShare && transcriptRevisions.length > 0 && (
                        <div className="space-y-2">
                          <div className={`text-xs font-semibold uppercase tracking-[0.16em] ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            Transcript Revisions
                          </div>
                          <TranscriptVersionOrbs
                            revisions={transcriptRevisions}
                            selectedChecksum={selectedTranscriptChecksum}
                            onSelectRevision={handleSelectRevision}
                            onViewDiff={handleViewDiffgram}
                            onDeleteRevision={handleDeleteRevision}
                            canDelete={!!token && !shareToken}
                          />
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-2">
                        {!isPublicShare && token && sessionId && (
                          <button
                            onClick={() => {
                              handleStartRefinement();
                              closeDesktopToolbarMenu();
                            }}
                            disabled={isRefining}
                            className={`inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm transition-colors disabled:cursor-wait disabled:opacity-70 ${
                              isDarkMode ? 'bg-gray-800 hover:bg-gray-700 text-gray-200' : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                            }`}
                            title="Start transcript refinement agent"
                          >
                            {isRefining ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} />}
                            <span>{isRefining ? 'Starting Agent…' : 'Refine'}</span>
                          </button>
                        )}

                        {!isPublicShare && selectedTranscriptChecksum && transcriptRevisions.find(r => r.checksum === selectedTranscriptChecksum)?.revision_notes && (
                          <button
                            onClick={() => {
                              handleViewRevisionNotes(selectedTranscriptChecksum);
                              closeDesktopToolbarMenu();
                            }}
                            className={`inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm transition-colors ${
                              isDarkMode ? 'bg-gray-800 hover:bg-gray-700 text-gray-200' : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                            }`}
                            title="View revision notes"
                          >
                            <FileSearch size={15} />
                            <span>Notes</span>
                          </button>
                        )}

                        {!isPublicShare && selectedTranscriptChecksum && transcriptRevisions.find(r => r.checksum === selectedTranscriptChecksum)?.parent_checksum && (
                          <button
                            onClick={() => {
                              handleViewDiffgram(selectedTranscriptChecksum);
                              closeDesktopToolbarMenu();
                            }}
                            className={`inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm transition-colors ${
                              isDarkMode ? 'bg-gray-800 hover:bg-gray-700 text-gray-200' : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                            }`}
                            title="View diffgram"
                          >
                            <GitCompare size={15} />
                            <span>Diffgram</span>
                          </button>
                        )}

                        <button
                          onClick={() => {
                            setShowWordCloud(true);
                            closeDesktopToolbarMenu();
                          }}
                          className={`inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm transition-colors ${
                            isDarkMode ? 'bg-gray-800 hover:bg-gray-700 text-gray-200' : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                          }`}
                          title="Show word cloud"
                        >
                          <Cloud size={15} />
                          <span>Word Cloud</span>
                        </button>

                        {token && (
                          <button
                            onClick={() => {
                              setGapModalOpen(true);
                              closeDesktopToolbarMenu();
                            }}
                            disabled={!sessionId || !effectiveTranscriptionData}
                            className={`inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm transition-colors ${
                              isDarkMode ? 'bg-gray-800 hover:bg-gray-700 text-gray-200' : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                            } disabled:opacity-50`}
                            title="Analyze speech gaps and pauses"
                          >
                            <Activity size={15} />
                            <span>Gap Analysis</span>
                          </button>
                        )}

                        <button
                          onClick={handleDownload}
                          className={`inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm transition-colors ${
                            isDarkMode ? 'bg-gray-800 hover:bg-gray-700 text-gray-200' : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                          }`}
                          title="Download TXT"
                        >
                          <FileText size={15} />
                          <span>TXT</span>
                        </button>

                        {!isPublicShare && (
                          <button
                            onClick={handleDownloadSRT}
                            disabled={!sessionId}
                            className={`inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm transition-colors ${
                              isDarkMode ? 'bg-gray-800 hover:bg-gray-700 text-gray-200' : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                            } disabled:opacity-50`}
                            title="Download SRT"
                          >
                            <Subtitles size={15} />
                            <span>SRT</span>
                          </button>
                        )}

                        {!isPublicShare && token && youtubeUrl && (
                          <button
                            onClick={handleUploadToYouTube}
                            disabled={isUploadingToYouTube}
                            className={`inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm transition-colors ${
                              youtubeUploadStatus === 'success'
                                ? 'bg-green-600 text-white'
                                : youtubeUploadStatus === 'error'
                                  ? 'bg-red-600 text-white'
                                  : isDarkMode
                                    ? 'bg-gray-800 hover:bg-gray-700 text-gray-200'
                                    : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                            } disabled:opacity-50`}
                            title={youtubeUploadStatus === 'success' ? 'Uploaded' : 'Upload SRT to YouTube'}
                          >
                            {isUploadingToYouTube ? (
                              <Loader2 size={15} className="animate-spin" />
                            ) : youtubeUploadStatus === 'success' ? (
                              <Check size={15} />
                            ) : (
                              <Upload size={15} />
                            )}
                            <span>YouTube Captions</span>
                          </button>
                        )}
                      </div>

                      {isRefining && (
                        <div className={`rounded-xl px-3 py-2 text-xs ${
                          isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-50 text-gray-600'
                        }`}>
                          Starting transcript refinement in the agent tab...
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      )}


      {/* Content Area */}
      <div className="flex-1 overflow-hidden relative">
        {isFullscreen && (
          <div
            className="pointer-events-none fixed right-4 z-50"
            style={{
              top: isCompactTouchLayout ? 'calc(var(--app-mobile-top-offset) + 0.75rem)' : '1rem',
            }}
          >
            <button
              type="button"
              onClick={toggleFullscreen}
              className={`pointer-events-auto rounded-full border p-3 shadow-lg backdrop-blur transition-colors ${
                isDarkMode
                  ? 'border-white/10 bg-[#232E3F]/95 text-white hover:border-[#FFB52B]/50 hover:bg-[#2D3A4F]'
                  : 'border-gray-200 bg-white/95 text-[#232E3F] hover:border-[#FFB52B]/50 hover:bg-[#FFF8EA]'
              }`}
              title="Exit fullscreen (ESC)"
              aria-label="Exit fullscreen"
            >
              <Minimize size={18} />
            </button>
          </div>
        )}

        {/* Floating Search Button - Mobile Only */}
        {!isFullscreen && isCompactTouchLayout && !showMobileSearchModal && (
          <div className="fixed bottom-32 right-4 z-50 flex flex-col items-end gap-2">
            <button
              type="button"
              onClick={toggleFullscreen}
              className={`rounded-full p-3 shadow-lg ${
                isDarkMode ? 'bg-[#232E3F] text-white' : 'bg-white text-[#232E3F]'
              }`}
              title="Maximize transcript"
            >
              <Maximize size={20} />
            </button>
            <button
              onClick={() => {
                setShowMobileSearchModal(true);
              }}
              className={`relative rounded-full p-3 shadow-lg ${
                searchQuery.trim()
                  ? 'bg-brand-yellow text-brand-navy'
                  : isDarkMode ? 'bg-primary-600 text-white' : 'bg-primary-500 text-white'
              }`}
              title="Search transcript"
            >
              <Search size={20} />
              {searchMatches.length > 0 && (
                <span className="absolute -right-1 -top-1 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-[#232E3F] px-1 text-[10px] font-semibold text-white">
                  {searchMatches.length > 99 ? '99+' : searchMatches.length}
                </span>
              )}
            </button>
          </div>
        )}

        {isCompactTouchLayout && showMobileSearchModal && (
          <div
            className={`fixed inset-x-0 z-[90] flex flex-col ${
              isDarkMode ? 'bg-[#111826]' : 'bg-[#f4f4f4]'
            }`}
            data-allow-touch-scroll="true"
            style={{
              top: 'var(--app-mobile-top-offset)',
              bottom: 'var(--app-mobile-bottom-offset)',
            }}
          >
            <div className={`flex items-center justify-between border-b px-4 py-3 ${
              isDarkMode ? 'border-gray-700 bg-[#16202c]' : 'border-gray-200 bg-white'
            }`}>
              <div>
                <div className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${
                  isDarkMode ? 'text-[#ffcf74]' : 'text-[#8a6115]'
                }`}>
                  Search
                </div>
                <div className={`text-sm font-semibold ${
                  isDarkMode ? 'text-white' : 'text-[#232E3F]'
                }`}>
                  Transcript search
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowMobileSearchModal(false)}
                className={`rounded-full border p-2 ${
                  isDarkMode
                    ? 'border-white/10 bg-white/[0.04] text-gray-300'
                    : 'border-gray-200 bg-[#f5f7fa] text-[#5b687c]'
                }`}
                aria-label="Close search"
              >
                <X size={16} />
              </button>
            </div>

            <div className={`border-b px-4 py-3 ${
              isDarkMode ? 'border-gray-700 bg-[#16202c]' : 'border-gray-200 bg-white'
            }`}>
              <div className={`flex items-center rounded-2xl px-3 py-2 ${
                isDarkMode ? 'bg-gray-800' : 'bg-[#eef1f3]'
              }`}>
                <Search size={16} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
                <input
                  ref={searchInputRef}
                  type="text"
                  inputMode="search"
                  autoCapitalize="none"
                  autoCorrect="off"
                  enterKeyHint="search"
                  value={searchQuery}
                  onFocus={(e) => e.currentTarget.select()}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && searchQuery.trim() !== '') {
                      setShouldNavigateToMatch(true);
                    }
                  }}
                  placeholder="Search transcript..."
                  className={`ml-3 min-w-0 flex-1 bg-transparent text-base outline-none ${
                    isDarkMode ? 'text-gray-100 placeholder-gray-500' : 'text-gray-700 placeholder-gray-400'
                  }`}
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    className={`ml-2 rounded-full p-1 ${
                      isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'
                    }`}
                    aria-label="Clear search"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>

              {searchMatches.length > 0 && (
                <div className={`mt-3 flex items-center justify-between text-xs ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  <span>{currentMatchIndex + 1} of {searchMatches.length} matches</span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        const newIndex = currentMatchIndex > 0 ? currentMatchIndex - 1 : searchMatches.length - 1;
                        setCurrentMatchIndex(newIndex);
                        setShouldNavigateToMatch(true);
                      }}
                      className={`rounded-full p-2 ${
                        isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-[#eef1f3] text-[#4a586e]'
                      }`}
                      disabled={searchMatches.length === 0}
                      aria-label="Previous match"
                    >
                      <ChevronLeft size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const newIndex = currentMatchIndex < searchMatches.length - 1 ? currentMatchIndex + 1 : 0;
                        setCurrentMatchIndex(newIndex);
                        setShouldNavigateToMatch(true);
                      }}
                      className={`rounded-full p-2 ${
                        isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-[#eef1f3] text-[#4a586e]'
                      }`}
                      disabled={searchMatches.length === 0}
                      aria-label="Next match"
                    >
                      <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div
              className={`flex-1 overflow-y-auto px-4 py-4 ${
                isDarkMode ? 'bg-[#111826]' : 'bg-[#f4f4f4]'
              }`}
              data-allow-touch-scroll="true"
            >
              {!searchQuery.trim() ? (
                <div className={`rounded-2xl border px-4 py-5 text-sm ${
                  isDarkMode ? 'border-gray-700 bg-[#16202c] text-gray-400' : 'border-gray-200 bg-white text-gray-600'
                }`}>
                  Enter a query to search the transcript.
                </div>
              ) : searchMatches.length === 0 ? (
                <div className={`rounded-2xl border px-4 py-5 text-sm ${
                  isDarkMode ? 'border-gray-700 bg-[#16202c] text-gray-400' : 'border-gray-200 bg-white text-gray-600'
                }`}>
                  No matches found for "{searchQuery}".
                </div>
              ) : (
                <div className="space-y-3">
                  {searchMatches.map((match, index) => (
                    <button
                      key={`${match.groupIndex}-${match.segmentIndex}-${index}`}
                      type="button"
                      onClick={() => {
                        setCurrentMatchIndex(index);
                        setShouldNavigateToMatch(true);
                        setShowMobileSearchModal(false);
                      }}
                      className={`w-full rounded-2xl border px-4 py-3 text-left transition-colors ${
                        index === currentMatchIndex
                          ? isDarkMode
                            ? 'border-[#ffcf74]/60 bg-[#1f2a3b] text-white'
                            : 'border-[#ffb52b]/70 bg-[#fff6dd] text-[#232E3F]'
                          : isDarkMode
                            ? 'border-gray-700 bg-[#16202c] text-white'
                            : 'border-gray-200 bg-white text-[#232E3F]'
                      }`}
                    >
                      <div className={`flex items-center justify-between gap-3 text-xs ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        <span className="truncate">{match.speaker || 'Speaker'}</span>
                        <span className="shrink-0">{match.timestamp}</span>
                      </div>
                      <div className={`mt-2 text-sm font-semibold ${
                        isDarkMode ? 'text-white' : 'text-[#232E3F]'
                      }`}>
                        {match.topic?.title ? String(match.topic.title).replace(/<[^>]+>/g, '').trim() : 'Transcript result'}
                      </div>
                      <div className={`mt-2 text-sm leading-6 ${
                        isDarkMode ? 'text-gray-300' : 'text-[#4a586e]'
                      }`}>
                        {match.contextStart > 0 && <span className="opacity-60">...</span>}
                        {match.contextText.substring(0, match.matchIndex)}
                        <span className={`rounded px-1 py-0.5 font-semibold ${
                          isDarkMode ? 'bg-[#ffcf74]/20 text-[#ffe09b]' : 'bg-[#ffe2a6] text-[#6e4d0e]'
                        }`}>
                          {match.contextText.substring(match.matchIndex, match.matchIndex + match.matchLength)}
                        </span>
                        {match.contextText.substring(match.matchIndex + match.matchLength)}
                        {match.contextStart + match.contextText.length < match.text.length && <span className="opacity-60">...</span>}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        
        {isTranscriptInitializing ? (
          <div className="flex h-full items-center justify-center px-6">
            <div className={`flex max-w-md items-center gap-3 rounded-2xl border px-5 py-4 shadow-sm ${
              isDarkMode
                ? 'border-[#39465A] bg-[#182130]/90 text-[#E7ECF2]'
                : 'border-[#D8DEE7] bg-white text-[#232E3F]'
            }`}>
              <Loader2 className="h-5 w-5 animate-spin text-brand-yellow" />
              <div>
                <div className="text-sm font-semibold">Loading transcript view</div>
                <div className={`mt-1 text-xs ${isDarkMode ? 'text-[#A7B2C4]' : 'text-[#66758A]'}`}>
                  Preparing transcript revisions and topic metadata before rendering.
                </div>
              </div>
            </div>
          </div>
        ) : hasRenderableTranscript && (
          <ReadableTranscriptView
            ttmlContent={selectedTtmlContent || ''}
            transcript={effectiveTranscriptText}
            filename={selectedTtmlName || filename}
            transcriptionData={effectiveTranscriptionData}
            speakerLabels={speakerLabels}
            speakerSegments={speakerSegments}
            transcriptionChecksum={transcriptionChecksum}
            sessionId={sessionId || undefined}
            isModal={false}
            onPlaySegment={handlePlaySegment}
            onPauseSegment={handlePauseSegment}
            showChat={false}
            comments={comments}
            isFullscreenTab={isFullscreen}
            searchQuery={searchQuery}
            searchMatches={searchMatches}
            currentMatchIndex={currentMatchIndex}
            shouldNavigateToMatch={shouldNavigateToMatch}
            onNavigationComplete={() => setShouldNavigateToMatch(false)}
            onSearchUpdate={handleSearchUpdate}
            onCurrentTopicChange={onCurrentTopicChange}
            showVideoPanel={showVideoPanel}
            typographyConfig={deferredTypographyConfig}
          />
        )}
      </div>
      
      {/* Word Cloud Modal */}
      <WordCloudModal
        isOpen={showWordCloud}
        onClose={() => setShowWordCloud(false)}
        transcript={transcript}
        filename={filename}
        speakerLabels={speakerLabels}
      />
      {/* Revision Notes Modal */}
      {showRevisionNotesModal && revisionNotesContent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowRevisionNotesModal(false)}
          />
          <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Revision Notes
              </h2>
              <button
                onClick={() => setShowRevisionNotesModal(false)}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              <pre className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300 font-mono">
                {revisionNotesContent}
              </pre>
            </div>
          </div>
        </div>
      )}

      {/* Diffgram Modal - Split Screen Layout */}
      {showDiffgramModal && currentDiffgram && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowDiffgramModal(false)}
          />
          <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Changes in This Revision ({currentDiffgram.edits.length} edits)
              </h2>
              <button
                onClick={() => setShowDiffgramModal(false)}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            {currentDiffgram.edits.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-gray-500 dark:text-gray-400">
                  No changes recorded in this revision.
                </p>
              </div>
            ) : (
              <div className="flex-1 overflow-hidden flex">
                {/* Left Column - Changes */}
                <div className="w-1/2 border-r border-gray-200 dark:border-gray-700 flex flex-col">
                  <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-300">Changes</span>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {currentDiffgram.edits.map((edit, index) => (
                      <div
                        key={edit.id}
                        className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                            #{index + 1} • {formatTimestampHHMMSS(edit.timeRange.startTime)}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            edit.type === 'replace' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' :
                            edit.type === 'insert' ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' :
                            'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                          }`}>
                            {edit.type}
                          </span>
                        </div>
                        <div className="space-y-1.5">
                          <div className="flex items-start gap-2">
                            <span className="text-xs text-red-500 font-medium shrink-0 mt-0.5">−</span>
                            <span className="text-sm text-red-600 dark:text-red-400 line-through">
                              {edit.original.text}
                            </span>
                          </div>
                          {edit.replacement && (
                            <div className="flex items-start gap-2">
                              <span className="text-xs text-green-500 font-medium shrink-0 mt-0.5">+</span>
                              <span className="text-sm text-green-600 dark:text-green-400">
                                {edit.replacement.text}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Right Column - Reasons */}
                <div className="w-1/2 flex flex-col">
                  <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-300">Reasons</span>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {currentDiffgram.edits.map((edit, index) => (
                      <div
                        key={edit.id}
                        className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900"
                      >
                        <div className="mb-2">
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                            #{index + 1} • {edit.original.text.substring(0, 30)}{edit.original.text.length > 30 ? '...' : ''} → {edit.replacement?.text.substring(0, 30)}{(edit.replacement?.text.length || 0) > 30 ? '...' : ''}
                          </span>
                        </div>
                        {edit.reason ? (
                          <div className="text-sm text-gray-700 dark:text-gray-300 prose prose-sm dark:prose-invert max-w-none">
                            <ReactMarkdown>{edit.reason}</ReactMarkdown>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400 dark:text-gray-500 italic">
                            No reason provided
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Gap Analysis Modal — authenticated users only */}
      {token && (
        <GapAnalysisModal
          isOpen={gapModalOpen}
          onClose={() => setGapModalOpen(false)}
          sessionId={sessionId || ''}
          speakerLabels={speakerLabels || {}}
          gapAnalysisData={gapAnalysisData}
          ttmlFiles={ttmlFiles}
          selectedTtmlId={selectedTtmlId}
          onFilteredClustersChange={onFilteredGapClustersChange}
        />
      )}
    </div>
  );
};

export default ReadableTranscriptTab;
